import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, UserPlus, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PatientForm, PATIENT_EMPTY, validatePatientForm, patientPayload } from "@/components/PatientForm";
import { useAuth } from "@/context/AuthContext";
import { dmyTime, today } from "@/lib/format";
import { firstError } from "@/lib/validate";

const EMPTY_APT = { doctor_id: "", department_id: "", date: today(), time: "10:00", reason: "", notes: "" };

export default function Appointments() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filterDay, setFilterDay] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("existing");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [patient, setPatient] = useState(PATIENT_EMPTY);
  const [patientErrors, setPatientErrors] = useState({});
  const [apt, setApt] = useState(EMPTY_APT);
  const [aptErrors, setAptErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resched, setResched] = useState(null);
  const [reschedAt, setReschedAt] = useState({ date: today(), time: "10:00" });

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/hospital/appointments", {
        params: { day: filterDay || undefined, status: status || undefined } });
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, [filterDay, status]);

  useEffect(() => {
    if (!can("hospital.appointments.manage")) return;
    load();
    Promise.all([api.get("/hospital/doctors"), api.get("/hospital/departments")])
      .then(([d, dep]) => { setDoctors(d.data.items); setDepartments(dep.data.items); })
      .catch(() => {});
  }, [load, can]);

  if (!can("hospital.appointments.manage")) return <NoAccess what="appointments" />;

  const isoFrom = ({ date, time }) => new Date(`${date}T${time}:00`).toISOString();

  const reset = () => {
    setOpen(false); setMode("existing"); setQ(""); setResults([]); setSelected(null);
    setPatient(PATIENT_EMPTY); setPatientErrors({}); setApt(EMPTY_APT); setAptErrors({});
    setFormError("");
  };

  const search = async (value) => {
    setQ(value);
    if (value.trim().length < 2) return setResults([]);
    try {
      const { data } = await api.get("/hospital/patients", { params: { q: value.trim(), limit: 8 } });
      setResults(data.items);
    } catch { setResults([]); }
  };

  const validateApt = () => {
    const e = {};
    if (!apt.doctor_id) e.doctor_id = "Doctor is required.";
    if (!apt.department_id) e.department_id = "Department is required.";
    if (!apt.date) e.date = "Appointment date is required.";
    if (!apt.time) e.time = "Appointment time is required.";
    if (!apt.reason.trim()) e.reason = "Reason for visit is required.";
    setAptErrors(e);
    return e;
  };

  const submit = async (e) => {
    e.preventDefault(); setFormError("");
    const aptErr = validateApt();
    const base = { doctor_id: apt.doctor_id, department_id: apt.department_id,
      scheduled_at: isoFrom(apt), reason: apt.reason, notes: apt.notes };
    if (Object.keys(aptErr).length && (mode === "new" || selected)) {
      setFormError(firstError(aptErr));
    }
    if (mode === "new") {
      const pErr = validatePatientForm(patient);
      setPatientErrors(pErr);
      if (Object.keys(pErr).length || Object.keys(aptErr).length) {
        setFormError(firstError(pErr) || firstError(aptErr));
        return;
      }
      setBusy(true);
      try {
        const { data } = await api.post("/hospital/appointments/with-patient",
          { patient: patientPayload(patient), ...base });
        toast.success(`Patient registered (UHID ${data.patient.uhid}) and appointment booked.`);
        reset(); load();
      } catch (err) { setFormError(apiError(err)); } finally { setBusy(false); }
      return;
    }
    if (!selected) { setFormError("Select an existing patient or register a new one."); return; }
    if (Object.keys(aptErr).length) { setFormError(firstError(aptErr)); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/hospital/appointments", { patient_id: selected.patient_id, ...base });
      toast.success("Appointment created." + (data.whatsapp === "sent" ? " WhatsApp confirmation sent." : ""));
      reset(); load();
    } catch (err) { setFormError(apiError(err)); } finally { setBusy(false); }
  };

  const setStatusOf = async (a, next) => {
    try {
      await api.post(`/hospital/appointments/${a.appointment_id}/status`, { status: next, reason: "Updated from console" });
      toast.success(`Appointment marked ${next.replace("_", " ")}.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doReschedule = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hospital/appointments/${resched.appointment_id}/reschedule`,
        { scheduled_at: isoFrom(reschedAt), reason: "Rescheduled by staff" });
      toast.success("Appointment rescheduled.");
      setResched(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remind = async (a) => {
    try {
      const { data } = await api.post(`/hospital/appointments/${a.appointment_id}/remind`);
      if (data.whatsapp === "sent") toast.success("WhatsApp reminder sent.");
      else if (data.whatsapp === "disabled") toast.info("Reminder skipped — the WhatsApp toggle for appointment reminders is disabled in Settings.");
      else toast.error(data.error || "Interakt did not accept the reminder. Check Settings → WhatsApp for details.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const tabCls = (on) => `inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium border ${
    on ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-700 hover:border-slate-900"}`;

  return (
    <div data-testid="appointments-page">
      <PageHeader overline="Hospital" title="Appointments"
        subtitle="Register a walk-in patient and book their slot in one screen. Doctor slots are protected against overlaps within 15 minutes."
        actions={<button onClick={() => setOpen(true)} className={primaryBtnCls} data-testid="add-appointment-btn">Book appointment</button>} />

      <div className="flex flex-wrap gap-3 mb-6">
        <input type="date" value={filterDay} onChange={(e) => setFilterDay(e.target.value)}
          data-testid="appointment-day-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="appointment-status-filter"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {["scheduled", "confirmed", "checked_in", "completed", "cancelled", "no_show"].map((s) =>
            <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        {(filterDay || status) && (
          <button onClick={() => { setFilterDay(""); setStatus(""); }} className={ghostBtnCls} data-testid="clear-appointment-filters">Clear</button>
        )}
      </div>

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="appointments-table" columns={["Date & time", "Patient", "Doctor", "Reason", "Status", "Actions"]}
          rows={items} empty={<EmptyState title="No appointments" subtitle="Book an appointment — new patients can be registered inside the same modal." testid="appointments-empty" />}
          renderRow={(a) => (
            <tr key={a.appointment_id} data-testid={`appointment-row-${a.appointment_id}`}>
              <Td className="whitespace-nowrap text-slate-700">{dmyTime(a.scheduled_at)}</Td>
              <Td className="font-medium text-slate-900">{a.patient_name}<span className="block text-xs text-slate-400">{a.patient_uhid}</span></Td>
              <Td className="text-slate-600">Dr. {a.doctor_name}<span className="block text-xs font-mono text-slate-400" data-testid={`appointment-no-${a.appointment_id}`}>{a.appointment_no || "—"}</span></Td>
              <Td className="text-slate-600">{a.reason || "—"}</Td>
              <Td><StatusPill status={a.status} /></Td>
              <Td>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setStatusOf(a, "checked_in")} className={ghostBtnCls} data-testid={`checkin-${a.appointment_id}`}>Check-in</button>
                  <button onClick={() => setStatusOf(a, "completed")} className={ghostBtnCls} data-testid={`complete-${a.appointment_id}`}>Complete</button>
                  <button onClick={() => { setResched(a); setReschedAt({ date: a.scheduled_at.slice(0, 10), time: "10:00" }); }} className={ghostBtnCls} data-testid={`reschedule-${a.appointment_id}`}>Reschedule</button>
                  <button onClick={() => remind(a)} className={ghostBtnCls} data-testid={`remind-${a.appointment_id}`}>Remind</button>
                  <button onClick={() => setStatusOf(a, "cancelled")} className={ghostBtnCls} data-testid={`cancel-${a.appointment_id}`}>Cancel</button>
                </div>
              </Td>
            </tr>
          )} />
      )}

      <Modal open={open} onClose={reset} wide title="Book appointment"
        subtitle="Existing patient or full new-patient registration — all in one workflow."
        testid="add-appointment-modal">
        <form onSubmit={submit} className="space-y-8">
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => setMode("existing")} className={tabCls(mode === "existing")}
              data-testid="appointment-existing-patient-btn">
              <Search className="h-4 w-4" /> Existing patient
            </button>
            <button type="button" onClick={() => { setMode("new"); setSelected(null); }}
              className={tabCls(mode === "new")} data-testid="appointment-new-patient-btn">
              <UserPlus className="h-4 w-4" /> + New patient
            </button>
          </div>

          {mode === "existing" ? (
            selected ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="appointment-selected-patient">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="flex items-center gap-2 font-semibold text-slate-900">
                      <Check className="h-4 w-4 text-emerald-700" /> {selected.name}
                    </p>
                    <p className="mt-1 text-xs text-slate-700">
                      UHID {selected.uhid} · {selected.gender}{selected.age ? ` · ${selected.age} yrs` : ""} · {selected.phone}
                    </p>
                  </div>
                  <button type="button" onClick={() => setSelected(null)} className={ghostBtnCls}
                    data-testid="appointment-change-patient-btn">Change</button>
                </div>
              </div>
            ) : (
              <div>
                <Field label="Search by mobile, name or UHID">
                  <input className={inputCls} value={q} data-testid="appointment-patient-search"
                    onChange={(e) => search(e.target.value)} placeholder="9876543210 / Ramesh / UH00012" />
                </Field>
                <div className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {results.length === 0 ? (
                    <p className="p-4 text-sm text-slate-500">Type at least 2 characters to search your patient records.</p>
                  ) : results.map((p) => (
                    <button type="button" key={p.patient_id} onClick={() => setSelected(p)}
                      data-testid={`appointment-patient-result-${p.patient_id}`}
                      className="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-slate-50">
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{p.name}</span>
                        <span className="block text-xs text-slate-600">UHID {p.uhid} · {p.phone} · {p.gender}</span>
                      </span>
                      <span className="text-xs font-medium text-[#EA580C]">Select</span>
                    </button>
                  ))}
                </div>
              </div>
            )
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-5">
              <PatientForm form={patient} setForm={setPatient} errors={patientErrors} prefix="apt-patient" />
            </div>
          )}

          <div className="border-t border-slate-200 pt-7">
            <p className="overline text-[#EA580C] mb-4">Appointment details</p>
            <div className="grid gap-5 sm:grid-cols-3">
              <Field label="Doctor *">
                <select className={inputCls} value={apt.doctor_id} data-testid="appointment-doctor-select"
                  onChange={(e) => {
                    const doc = doctors.find((d) => d.doctor_id === e.target.value);
                    setApt({ ...apt, doctor_id: e.target.value,
                      department_id: doc?.department_id || apt.department_id });
                  }}>
                  <option value="">Select doctor</option>
                  {doctors.map((d) => <option key={d.doctor_id} value={d.doctor_id}>Dr. {d.name}{d.specialization ? ` · ${d.specialization}` : ""}</option>)}
                </select>
                {aptErrors.doctor_id && <p className="mt-1.5 text-xs font-medium text-red-600">{aptErrors.doctor_id}</p>}
              </Field>
              <Field label="Department *">
                <select className={inputCls} value={apt.department_id} data-testid="appointment-department-select"
                  onChange={(e) => setApt({ ...apt, department_id: e.target.value })}>
                  <option value="">Select department</option>
                  {departments.map((d) => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
                </select>
                {aptErrors.department_id && <p className="mt-1.5 text-xs font-medium text-red-600">{aptErrors.department_id}</p>}
              </Field>
              <Field label="Reason for visit *">
                <input className={inputCls} value={apt.reason} data-testid="appointment-reason-input"
                  onChange={(e) => setApt({ ...apt, reason: e.target.value })} />
                {aptErrors.reason && <p className="mt-1.5 text-xs font-medium text-red-600">{aptErrors.reason}</p>}
              </Field>
              <Field label="Date *">
                <input type="date" className={inputCls} value={apt.date} data-testid="appointment-date-input"
                  onChange={(e) => setApt({ ...apt, date: e.target.value })} />
                {aptErrors.date && <p className="mt-1.5 text-xs font-medium text-red-600">{aptErrors.date}</p>}
              </Field>
              <Field label="Time *">
                <input type="time" className={inputCls} value={apt.time} data-testid="appointment-time-input"
                  onChange={(e) => setApt({ ...apt, time: e.target.value })} />
                {aptErrors.time && <p className="mt-1.5 text-xs font-medium text-red-600">{aptErrors.time}</p>}
              </Field>
              <Field label="Notes">
                <input className={inputCls} value={apt.notes} data-testid="appointment-notes-input"
                  onChange={(e) => setApt({ ...apt, notes: e.target.value })} />
              </Field>
            </div>
          </div>

          {formError && <p className="text-sm font-medium text-red-600" data-testid="appointment-form-error">{formError}</p>}
          <div className="flex flex-wrap gap-3">
            <button type="submit" disabled={busy} className={primaryBtnCls} data-testid="appointment-submit-btn">
              {mode === "new" ? "Register patient & book appointment" : "Create appointment"}
            </button>
            <button type="button" onClick={reset} className={ghostBtnCls} data-testid="appointment-cancel-btn">Cancel</button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(resched)} onClose={() => setResched(null)} title="Reschedule appointment" testid="reschedule-modal">
        <form onSubmit={doReschedule} className="grid gap-5 sm:grid-cols-2">
          <Field label="New date"><input type="date" className={inputCls} value={reschedAt.date} data-testid="reschedule-date-input" onChange={(e) => setReschedAt({ ...reschedAt, date: e.target.value })} /></Field>
          <Field label="New time"><input type="time" className={inputCls} value={reschedAt.time} data-testid="reschedule-time-input" onChange={(e) => setReschedAt({ ...reschedAt, time: e.target.value })} /></Field>
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="reschedule-submit-btn">Confirm reschedule</button></div>
        </form>
      </Modal>
    </div>
  );
}
